
  import { defineComponent } from 'vue';
  import { mapActions, mapGetters } from 'vuex';
  import type { Feature } from 'geojson';
  import type { PathOptions } from 'leaflet';

  import MapComponent from '@/common/components/MapComponent/MapComponent.vue';
  import type { GeoJsonObject, MapConfig, MapLegendConfig, MapPopupRow } from '@/common/components/MapComponent/types';
  import RedesignedSwitchComponent from '@/common/components/redesigned/RedesignedSwitchComponent.vue';
  import Constants from '@/common/constants';
  import { WORLD_MAP_CONFIG } from '@/common/constants/mapConfig';
  import { TRADE_FLOW_TYPES } from '@/common/constants/tradeFlow';
  import worldMap from '@/common/custom.geo.json';
  import type { CountryShortDto } from '@/common/types/country';
  import {
    buildGradientCss,
    buildRangeMarks,
    getGradientColor,
    getValuesRange,
    parsePercent,
  } from '@/common/utils/mapGradient';
  import { numberFormatter, numberFormatterWithFixedFractionDigits } from '@/common/utils/numberFormatter';

  import Api from '../../../api';
  import {
    LEGEND_POSITIONS,
    LEGEND_STOPS,
    SHARE_LIGHTNESS,
    TRADE_BASE_HSL,
    TRADE_MAP_COLORS,
    TRADE_MAP_ERRORS,
  } from './tradeWorldMap';
  import type { TradeFlowOption, TradePartner, TradeWorldMapData } from './types';

  import { DIRECTIVES_ACTIONS_NAME, DIRECTIVES_GETTERS_NAME } from '@/store/directives';

  export default defineComponent({
    name: 'TradeWorldMap',
    components: { MapComponent, RedesignedSwitchComponent },
    props: {
      countryId: {
        type: String,
        default: '',
      },
      year: {
        type: [String, Number],
        default: null,
      },
    },
    data(): TradeWorldMapData {
      return {
        tradeFlowType: Constants.tradingOperation.export,
        partners: [],
        isLoading: false,
        errorMessage: '',
        selectedFeatureCode: null,
        popup: {
          isOpen: false,
          partnerFullName: '',
          value: null,
          sharePercent: null,
          changePercent: null,
        },
      };
    },
    async created() {
      await this.getNsiCountryWithData();
      this.loadPartners();
    },
    computed: {
      ...mapGetters({
        getNsiCountryListWithData: DIRECTIVES_GETTERS_NAME.getNsiCountryListWithData,
      }),
      mapConfig(): MapConfig {
        return WORLD_MAP_CONFIG;
      },
      getGeojson(): GeoJsonObject {
        return worldMap as unknown as GeoJsonObject;
      },
      getFlowTypes(): TradeFlowOption[] {
        return TRADE_FLOW_TYPES;
      },
      getCurrentFlow(): TradeFlowOption {
        return this.getFlowTypes.find((flow) => flow.id === this.tradeFlowType) ?? this.getFlowTypes[0];
      },
      getCountries(): CountryShortDto[] {
        return this.getNsiCountryListWithData;
      },
      getSelectedCountryCode(): Nullable<number> {
        return this.getCountries.find((country) => country.id === Number(this.countryId))?.code ?? null;
      },
      getCountryNameByCode(): Record<number, string> {
        return this.getCountries.reduce<Record<number, string>>((acc, country) => {
          if (country.code != null) acc[country.code] = country.nameFull || country.name;
          return acc;
        }, {});
      },
      getPartnersByCode(): Record<number, TradePartner> {
        return this.partners.reduce<Record<number, TradePartner>>((acc, partner) => {
          const code = this.getCountries.find((country) => country.id === partner.partnerId)?.code;
          if (code != null) acc[code] = partner;
          return acc;
        }, {});
      },
      getShareRange() {
        const shares = this.partners
          .filter((partner) => partner.value)
          .map((partner) => parsePercent(partner.sharePercent))
          .filter((share): share is number => share != null);

        return { min: 0, max: getValuesRange(shares).max };
      },
      getStateMessage(): string {
        if (this.errorMessage) return this.errorMessage;
        return this.partners.length ? '' : TRADE_MAP_ERRORS.EMPTY;
      },
      getLegend(): MapLegendConfig {
        return {
          title: `Доля в ${this.getCurrentFlow.prepositionalLabel}, %`,
          background: buildGradientCss(
            TRADE_BASE_HSL[this.tradeFlowType],
            this.getShareRange,
            LEGEND_STOPS,
            SHARE_LIGHTNESS,
            TRADE_MAP_COLORS.NO_DATA,
            LEGEND_POSITIONS,
          ),
          marks: buildRangeMarks(this.getShareRange, (value) => numberFormatter(value, 'ru-RU', 1)),
        };
      },
      getPopupRows(): MapPopupRow[] {
        const rows: MapPopupRow[] = [
          { label: this.getCurrentFlow.label, value: this.formatValue(this.popup.value), postfix: 'млрд $' },
          {
            label: 'Доля',
            value: this.formatShare(this.popup.sharePercent),
            show: !!this.popup.sharePercent,
            postfix: '%',
          },
          {
            label: 'Изменение',
            value: this.popup.changePercent != null ? this.formatChange(this.popup.changePercent) : '',
            show: this.popup.changePercent != null,
          },
        ];

        return rows.filter((row) => row.show ?? true);
      },
    },
    methods: {
      ...mapActions({
        getNsiCountryWithData: DIRECTIVES_ACTIONS_NAME.getNsiCountryWithData,
      }),

      async loadPartners() {
        this.closePopup();
        this.isLoading = true;
        this.errorMessage = '';

        try {
          const response = await Api.getTradePartners(Number(this.countryId), Number(this.year), this.tradeFlowType);
          this.partners = response.data ?? [];
        } catch (error) {
          this.partners = [];
          this.errorMessage = TRADE_MAP_ERRORS[error?.response?.status] ?? TRADE_MAP_ERRORS[500];
        } finally {
          this.isLoading = false;
        }
      },

      getFeatureCode(feature?: Feature): number {
        return Number(feature?.properties?.iso_n3);
      },

      getPartnerByFeature(feature?: Feature): Nullable<TradePartner> {
        return this.getPartnersByCode[this.getFeatureCode(feature)] ?? null;
      },

      isSelectedCountry(feature?: Feature): boolean {
        return this.getSelectedCountryCode != null && this.getFeatureCode(feature) === this.getSelectedCountryCode;
      },

      hasPartnerValue(partner: Nullable<TradePartner>): partner is TradePartner {
        return !!partner?.value;
      },

      styleFeature(feature?: Feature): PathOptions {
        const baseStyle = { color: TRADE_MAP_COLORS.BORDER, weight: 0.5, fillOpacity: 1 };

        if (this.isSelectedCountry(feature)) {
          return { ...baseStyle, fillColor: TRADE_MAP_COLORS.SELECTED_COUNTRY };
        }

        const partner = this.getPartnerByFeature(feature);
        const share = parsePercent(partner?.sharePercent);

        if (!this.hasPartnerValue(partner) || share == null) {
          return { ...baseStyle, fillColor: TRADE_MAP_COLORS.NO_DATA };
        }

        return {
          ...baseStyle,
          fillColor: getGradientColor(share, TRADE_BASE_HSL[this.tradeFlowType], this.getShareRange, SHARE_LIGHTNESS),
        };
      },

      featureTooltip(feature?: Feature): string {
        return this.getCountryNameByCode[this.getFeatureCode(feature)] ?? '';
      },

      onFeatureClick(feature?: Feature): void {
        const partner = this.getPartnerByFeature(feature);
        const isSelectedCountry = this.isSelectedCountry(feature);

        if (!isSelectedCountry && !this.hasPartnerValue(partner)) return;

        this.selectedFeatureCode = this.getFeatureCode(feature);

        this.popup =
          isSelectedCountry || !this.hasPartnerValue(partner)
            ? { ...this.popup, isOpen: false }
            : {
                isOpen: true,
                partnerFullName: this.featureTooltip(feature),
                value: partner.value,
                sharePercent: partner.sharePercent ?? null,
                changePercent: partner.changePercent ?? null,
              };
      },

      onFlowSwitch(isExport: boolean): void {
        const flowType = isExport ? Constants.tradingOperation.export : Constants.tradingOperation.import;
        if (this.tradeFlowType === flowType) return;

        this.tradeFlowType = flowType;
        this.selectedFeatureCode = null;
        this.loadPartners();
      },

      closePopup() {
        this.popup.isOpen = false;
        this.selectedFeatureCode = null;
      },

      formatValue(value: Nullable<number>): string {
        return numberFormatterWithFixedFractionDigits(value ?? 0, 'ru-RU', 1);
      },

      formatShare(sharePercent: TradePartner['sharePercent']): string {
        const value = parsePercent(sharePercent);
        return value != null ? numberFormatterWithFixedFractionDigits(value, 'ru-RU', 1) : '';
      },

      formatChange(changePercent: number): string {
        if (changePercent === 0) return '-';

        const sign = changePercent > 0 ? '+' : '-';
        return `${sign}${numberFormatterWithFixedFractionDigits(Math.abs(changePercent), 'ru-RU', 1)}% г/г`;
      },
    },
    watch: {
      countryId() {
        this.selectedFeatureCode = null;
        this.loadPartners();
      },
      year() {
        this.loadPartners();
      },
    },
  });
